import { create } from 'zustand';
import type {
  AssistantItem,
  AssistantVisualGroup,
  UserVisualGroup,
  VisualGroup,
} from '../components/agent/groupTurns';
import {
  isConfirmationRequest,
  type ConfirmationRequestContent,
} from '../types';

export type StreamStatus = 'idle' | 'streaming' | 'error';

export interface CatalogChatSlice {
  /** Visual groups produced by streams completed in this browser session,
   *  appended after any BE-replayed turns. Cleared by an explicit replay
   *  refresh (catalog re-entry past staleTime). */
  localGroups: VisualGroup[];
  /** The user's just-sent message, rendered optimistically while the stream
   *  is in flight. Moves into `localGroups` on stream completion. */
  pendingUserText: string | null;
  /** The streaming assistant turn currently growing from SSE events. */
  inFlight: AssistantVisualGroup | null;
  status: StreamStatus;
  /** Error surface for failed streams (transport or protocol). Cleared on
   *  the next successful send. */
  error: string | null;
  /** Aborts the in-flight fetch. Per SC-1612 catalog-switch-mid-stream
   *  decision, the abort is owned by the slice and survives mounts. */
  abortController: AbortController | null;
  /** `dataUpdatedAt` of the last replay query result that drove a
   *  `localGroups` clear. Persisted at slice scope (not as a hook ref) so a
   *  plain unmount-then-remount with cached data does NOT trigger a spurious
   *  clear — only an actual refetch with a new timestamp does. */
  lastReplayUpdatedAt: number | null;
}

interface AgentChatStore {
  byCatalog: Record<string, CatalogChatSlice>;
  beginSend: (
    catalogId: string,
    userText: string,
    abortController: AbortController,
  ) => void;
  appendDelta: (catalogId: string, text: string) => void;
  appendToolStart: (
    catalogId: string,
    payload: { id: string; tool: string; display_name: string; input: unknown },
  ) => void;
  resolveTool: (
    catalogId: string,
    payload: {
      id: string;
      summary: string;
      isError: boolean;
      // Structured tool output forwarded from the SSE `tool_use_done` event.
      // SC-2157 confirmation round-trip discriminates on `content.needs_confirmation`.
      content?: Record<string, unknown> | string;
    },
  ) => void;
  finishStream: (catalogId: string) => void;
  failStream: (catalogId: string, message: string) => void;
  clearLocalGroups: (catalogId: string) => void;
  /** Call after the replay query lands fresh data. Clears `localGroups`
   *  iff `updatedAt` differs from the last consumed timestamp, so cached-mount
   *  re-renders are no-ops but actual refetches wipe the local overlay. */
  consumeReplayUpdate: (catalogId: string, updatedAt: number) => void;
  resetCatalog: (catalogId: string) => void;
  /** Wipe ALL per-catalog chat state on an identity change (logout / login),
   *  via `clearSessionState` (SC-2461). Aborts every in-flight stream first so
   *  a late SSE completion can't repopulate the store for the previous account
   *  after it's been cleared. */
  reset: () => void;
}

export const EMPTY_SLICE: CatalogChatSlice = {
  localGroups: [],
  pendingUserText: null,
  inFlight: null,
  status: 'idle',
  error: null,
  abortController: null,
  lastReplayUpdatedAt: null,
};

function getSlice(
  state: AgentChatStore,
  catalogId: string,
): CatalogChatSlice {
  return state.byCatalog[catalogId] ?? EMPTY_SLICE;
}

function withSlice(
  state: AgentChatStore,
  catalogId: string,
  patch: Partial<CatalogChatSlice>,
): Pick<AgentChatStore, 'byCatalog'> {
  return {
    byCatalog: {
      ...state.byCatalog,
      [catalogId]: { ...getSlice(state, catalogId), ...patch },
    },
  };
}

function mutateInFlight(
  state: AgentChatStore,
  catalogId: string,
  mutate: (group: AssistantVisualGroup) => AssistantVisualGroup,
): Pick<AgentChatStore, 'byCatalog'> | null {
  const slice = getSlice(state, catalogId);
  if (!slice.inFlight) return null;
  return withSlice(state, catalogId, { inFlight: mutate(slice.inFlight) });
}

function appendOrExtendText(
  items: AssistantItem[],
  text: string,
): AssistantItem[] {
  const last = items[items.length - 1];
  if (last && last.kind === 'text') {
    return [
      ...items.slice(0, -1),
      { kind: 'text', text: last.text + text },
    ];
  }
  return [...items, { kind: 'text', text }];
}

function nextLocalTurnId(prefix: string): string {
  return `${prefix}-${crypto.randomUUID()}`;
}

export const useAgentChatStore = create<AgentChatStore>((set) => ({
  byCatalog: {},

  beginSend: (catalogId, userText, abortController) =>
    set((state) => {
      const slice = getSlice(state, catalogId);
      if (slice.status === 'streaming') return state;
      return withSlice(state, catalogId, {
        pendingUserText: userText,
        inFlight: {
          kind: 'assistant',
          turnIds: [nextLocalTurnId('local-asst')],
          items: [],
        },
        status: 'streaming',
        error: null,
        abortController,
      });
    }),

  appendDelta: (catalogId, text) =>
    set((state) => {
      const patch = mutateInFlight(state, catalogId, (group) => ({
        ...group,
        items: appendOrExtendText(group.items, text),
      }));
      return patch ?? state;
    }),

  appendToolStart: (catalogId, { id, tool, display_name, input }) =>
    set((state) => {
      const patch = mutateInFlight(state, catalogId, (group) => ({
        ...group,
        items: [
          ...group.items,
          {
            kind: 'tool',
            id,
            name: tool,
            displayName: display_name,
            input,
            result: undefined,
          },
        ],
      }));
      return patch ?? state;
    }),

  resolveTool: (catalogId, { id, summary, isError, content }) =>
    set((state) => {
      const patch = mutateInFlight(state, catalogId, (group) => {
        // Resolve the most recent tool item with this id. Defensive: if not
        // found, ignore — the BE shouldn't emit done without a matching start.
        for (let i = group.items.length - 1; i >= 0; i -= 1) {
          const item = group.items[i];
          if (item.kind === 'tool' && item.id === id) {
            const next = [...group.items];
            const confirmation =
              isConfirmationRequest(content)
                ? (content as ConfirmationRequestContent)
                : undefined;
            next[i] = {
              ...item,
              result: { summary, isError, content },
              confirmation,
            };
            return { ...group, items: next };
          }
        }
        return group;
      });
      return patch ?? state;
    }),

  finishStream: (catalogId) =>
    set((state) => {
      const slice = getSlice(state, catalogId);
      const finalized: VisualGroup[] = [];
      if (slice.pendingUserText !== null) {
        const userGroup: UserVisualGroup = {
          kind: 'user',
          turnId: nextLocalTurnId('local-user'),
          blocks: [{ type: 'text', text: slice.pendingUserText }],
        };
        finalized.push(userGroup);
      }
      if (slice.inFlight && slice.inFlight.items.length > 0) {
        finalized.push(slice.inFlight);
      }
      return withSlice(state, catalogId, {
        localGroups: [...slice.localGroups, ...finalized],
        pendingUserText: null,
        inFlight: null,
        status: 'idle',
        error: null,
        abortController: null,
      });
    }),

  failStream: (catalogId, message) =>
    set((state) =>
      // Keep pendingUserText and inFlight visible so the user sees what got
      // partially done; the next successful send clears them by transitioning
      // through beginSend.
      withSlice(state, catalogId, {
        status: 'error',
        error: message,
        abortController: null,
      }),
    ),

  clearLocalGroups: (catalogId) =>
    set((state) => withSlice(state, catalogId, { localGroups: [] })),

  consumeReplayUpdate: (catalogId, updatedAt) =>
    set((state) => {
      const slice = getSlice(state, catalogId);
      if (slice.lastReplayUpdatedAt === updatedAt) return state;
      return withSlice(state, catalogId, {
        localGroups: [],
        lastReplayUpdatedAt: updatedAt,
      });
    }),

  resetCatalog: (catalogId) =>
    set((state) => {
      const next = { ...state.byCatalog };
      delete next[catalogId];
      return { byCatalog: next };
    }),

  reset: () =>
    set((state) => {
      // Abort any open SSE stream before dropping its slice — otherwise a
      // stream that completes after the reset would re-add a finished group
      // (finishStream re-creates a slice for its catalogId) for an account
      // that's no longer signed in.
      for (const slice of Object.values(state.byCatalog)) {
        slice.abortController?.abort();
      }
      return { byCatalog: {} };
    }),
}));
